import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Workforce } from "@/types/workforce";
import { WorkforceEditTabs } from "./WorkforceEditTabs";
import { toast } from "@/hooks/use-toast";

interface WorkforceEditModalProps {
	workforceId: string | null;
	onOpenChange: (open: boolean) => void;
	onSuccess?: () => void;
}

export const WorkforceEditModal = ({ workforceId, onOpenChange, onSuccess }: WorkforceEditModalProps) => {
	const { t } = useTranslation();
	const [workforce, setWorkforce] = useState<Workforce | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (!workforceId) {
			setWorkforce(null);
			return;
		}

		const fetchWorkforce = async () => {
			setIsLoading(true);
			try {
				const { data, error } = await supabase
					.from('workforce')
					.select('*')
					.eq('id', workforceId)
					.single();

				if (error) throw error;
				setWorkforce(data as Workforce);
			} catch (error) {
				toast({
					title: t("common.error", "Error"),
					description: t("workforce.editLoadFailed", "Failed to load assignment"),
					variant: "destructive",
				});
				onOpenChange(false);
			} finally {
				setIsLoading(false);
			}
		};

		fetchWorkforce();
	}, [workforceId]);

	const handleFieldChange = (field: string, value: any) => {
		if (workforce)
			setWorkforce({ ...workforce, [field]: value });
	};

	const handleSave = async () => {
		if (!workforce || !workforceId) return;

		setIsSaving(true);
		try {
			const updateData: any = { ...workforce };
			delete updateData.candidate;
			delete updateData.job;
			delete updateData.assigned_recruiter_profile;

			const { error } = await supabase
				.from('workforce')
				.update(updateData)
				.eq('id', workforceId);

			if (error) throw error;

			toast({
				title: t("common.success", "Success"),
				description: t("workforce.updateSuccess", "Assignment updated successfully"),
			});
			onOpenChange(false);
			onSuccess?.();
		} catch (error) {
			toast({
				title: t("common.error", "Error"),
				description: t("workforce.updateError", "Failed to update assignment"),
				variant: "destructive",
			});
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Dialog open={!!workforceId} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>
						{t("workforce.editTitle", "Edit Assignment")}
					</DialogTitle>
				</DialogHeader>

				{isLoading ? (
					<div className="flex items-center justify-center py-8">
						<Loader2 className="h-6 w-6 animate-spin text-primary" />
					</div>
				) : workforce ? (
					<WorkforceEditTabs workforce={workforce} onChange={handleFieldChange} />
				) : null}

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
						{t("common.cancel", "Cancel")}
					</Button>
					<Button onClick={handleSave} disabled={isLoading || isSaving}>
						{isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
						{t("common.save", "Save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
